/**
 * 生命健康 H · H-Score™ 健康资本
 *
 * 产品目标（沿用原型的产品判断）：
 *   把 H-Score™ 从「一个分数」变成「可追问的解释」——为什么是这个分、由什么构成、怎么改。
 *   因此 XAI 评分归因与主分数同屏，不做二级页；这是 HEW³ 与传统健康 App 最本质的差别：
 *   传统 App 只给结论，HEW³ 同时给结论、归因与下一步动作。
 *
 * 与原型的差别：
 *   - 所有数值（分数、年龄、指标规模、贡献度、置信度、预计提升）均来自
 *     GET /api/health/overview，页面上没有写死的业务数字；
 *   - 六层指标为描述性呈现（源材料未给出每层项数），平台总数取口径常量；
 *   - 预警「确认收到」真实落库（POST /api/health/alerts/:id/acknowledge）并写入审计留痕。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { Alert, HealthOverview, HealthVital, Prescription } from '@hew3/shared';
import { alertLevels, resolveColor, skCaliber } from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { charts } from '../../lib/charts.ts';
import { formatDate, formatPct, signed } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import { Badge, Button, Card, Chart, StateBox, Table, Timeline } from '../../components/ui.tsx';

/* ---------------- 局部映射（只做「数据 → 文案/颜色」，不含业务数字） ---------------- */

const vitalStatusText: Record<HealthVital['status'], string> = {
  good: '正常',
  watch: '需关注',
  risk: '异常',
  // 报告解析出来但缺参考区间的指标：不能默认说「正常」
  unknown: '待判读',
};

const prescriptionStatus: Record<Prescription['status'], { label: string; tone: 'h' | 'e' | 'ok' }> = {
  ongoing: { label: '执行中', tone: 'h' },
  new: { label: '新推送', tone: 'e' },
  done: { label: '已完成', tone: 'ok' },
};

/**
 * 预警等级 → 呈现参数。
 *
 * 契约：`AlertLevel` 就是接口返回值本身（'info' | 'warn' | 'danger'），
 * 因此可以直接作为 `alertLevels` 的键使用，不需要再做字符串兜底匹配。
 */
function alertColor(level: Alert['level']): string {
  return resolveColor(alertLevels[level]?.color ?? 'info');
}

function alertTone(level: Alert['level']): 'warn' | 'danger' | 'ghost' {
  if (level === 'danger') return 'danger';
  if (level === 'warn') return 'warn';
  return 'ghost';
}

function vitalColor(status: HealthVital['status']): string {
  if (status === 'risk') return resolveColor('danger');
  if (status === 'watch') return resolveColor('warn');
  // 待判读用中性色：既不能显示成绿色的「正常」，也不该显示成红色的警报
  if (status === 'unknown') return resolveColor('ghost');
  return resolveColor('ok');
}

/** 未解除预警卡片：标题 / 描述 / 建议动作 / 置信度 / 发现时间 / 确认收到 */
function AlertCard({
  alert,
  busy,
  onAcknowledge,
}: {
  alert: Alert;
  busy: boolean;
  onAcknowledge: () => void;
}) {
  const color = alertColor(alert.level);
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        marginBottom: 10,
        border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
        background: `color-mix(in srgb, ${color} 8%, transparent)`,
      }}
    >
      <div className="row between" style={{ marginBottom: 8 }}>
        <Badge tone={alertTone(alert.level)}>{alert.levelLabel}</Badge>
        <span className="num" style={{ fontSize: 11.5, color: 'var(--muted-2)' }}>
          {formatDate(alert.foundAt)} 发现
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color, lineHeight: 1.5 }}>{alert.title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.7, marginTop: 7 }}>{alert.desc}</div>
      <div
        style={{
          fontSize: 12.5,
          color: 'var(--text-2)',
          lineHeight: 1.7,
          marginTop: 9,
          paddingTop: 9,
          borderTop: '1px dashed var(--line-2)',
        }}
      >
        建议动作：{alert.action}
      </div>
      <div className="row between" style={{ marginTop: 11 }}>
        <span className="data-tag" style={{ fontSize: 12 }}>
          {alert.confidenceText}
        </span>
        {alert.status === 'open' ? (
          <Button small variant="primary" disabled={busy} onClick={onAcknowledge}>
            {busy ? '提交中…' : '确认收到'}
          </Button>
        ) : (
          <span className="badge ghost">已确认收到</span>
        )}
      </div>
    </div>
  );
}

export function HealthPage() {
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);

  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<HealthOverview>('/api/health/overview'),
  });

  const acknowledge = useMutation({
    mutationFn: (id: string) => api.post<Alert>(`/api/health/alerts/${id}/acknowledge`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['health'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      showToast('已确认收到，处置流程已启动');
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : '确认失败，请稍后重试', 'error');
    },
  });

  if (healthQuery.isLoading) {
    return (
      <div className="page-pad">
        <div className="skeleton skel-card" style={{ height: 170 }} />
        <div className="skeleton skel-card" style={{ height: 150 }} />
        <div className="skeleton skel-card" style={{ height: 280 }} />
        <div className="skeleton skel-card" />
      </div>
    );
  }

  if (healthQuery.isError || !healthQuery.data) {
    return (
      <StateBox
        kind="error"
        title="健康数据加载失败"
        desc="未能取回 H-Score™ 评估结果。请检查网络后重试；若持续失败，可稍后从首页进入。"
        action={
          <button type="button" className="btn sm" onClick={() => void healthQuery.refetch()}>
            重新加载
          </button>
        }
      />
    );
  }

  const data = healthQuery.data;

  /* ---------------- 全部数值由接口数据推导，前端不写死业务数字 ---------------- */
  const younger = data.realAge - data.bioAge;
  // 指标规模统一取口径常量（见下方「多模态指标体系」卡片）：
  // 分层视图不含每层项数 —— 源材料未给出该分布，按层分摊数字属于编造。
  const xaiAbsTotal = data.xai.items.reduce((sum, i) => sum + Math.abs(i.signedValue), 0) || 1;
  const xaiMax = data.xai.items.reduce((max, i) => Math.max(max, Math.abs(i.signedValue)), 1);
  const ongoingGain = data.prescriptions
    .filter((p) => p.status !== 'done')
    .reduce((sum, p) => sum + p.expectedGain, 0);
  const reports = [...data.reports].sort((a, b) => (a.reportedAt < b.reportedAt ? 1 : -1));

  const healthColor = resolveColor('h');
  const softBox = (key: 'h' | 'ok' | 'danger' | 'warn' | 'e') => ({
    padding: '11px 12px',
    borderRadius: 12,
    fontSize: 12.5,
    lineHeight: 1.75,
    color: 'var(--text-2)',
    background: `color-mix(in srgb, ${resolveColor(key)} 10%, transparent)`,
    border: `1px solid color-mix(in srgb, ${resolveColor(key)} 26%, transparent)`,
  });

  return (
    <div className="page">
      <div className="scr-head">
        <div className="scr-title">
          生命健康 H<small>H-SCORE™ 健康资本量化评估</small>
        </div>
      </div>

      {/* ---------------- 1. 主分数环 + 数据截至说明 ---------------- */}
      <Card>
        <div className="row" style={{ gap: 14, alignItems: 'center' }}>
          <div style={{ flex: '0 0 auto' }}>
            <Chart
              html={charts.ring({
                value: data.score,
                max: 100,
                size: 128,
                thickness: 9,
                color: 'h',
                label: 'H-Score™',
              })}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Badge tone="h">
              {data.grade} · {data.gradeText}
            </Badge>
            <div
              className={`num ${data.delta.direction === 'down' ? 'down' : 'up'}`}
              style={{ fontSize: 14, fontWeight: 600, marginTop: 8 }}
            >
              {data.delta.direction === 'down' ? '▼' : '▲'} {data.delta.display}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, lineHeight: 1.7 }}>
              上次评估 {data.prevScore} 分 · 半年前 {data.halfYearAgoScore} 分
            </div>
          </div>
        </div>

        <div className="divider" />

        {/* 诚实标注：这个分数是什么时候、依据什么算出来的 */}
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.75 }}>
          <span className="data-tag" style={{ fontSize: 12 }}>
            数据截至 {formatDate(data.measuredAt)}
          </span>
          <div style={{ marginTop: 8 }}>数据来源：{data.dataSource}</div>
        </div>
      </Card>

      {/* ---------------- 2. 生物年龄对比 ---------------- */}
      <Card title="生物年龄 vs. 实际年龄" more={<Badge tone="h">年轻化指标</Badge>}>
        <Chart
          html={charts.vbars({
            items: [
              { label: '生物年龄', value: data.bioAge, unit: ' 岁', color: 'h' },
              { label: '器官年龄', value: data.organAge, unit: ' 岁', color: 'e' },
              { label: '实际年龄', value: data.realAge, unit: ' 岁', color: 'muted' },
            ],
            h: 92,
          })}
        />
        <div className="sp12" />
        <div style={softBox('h')}>
          <b style={{ color: healthColor }}>
            {younger > 0
              ? `比实际年龄年轻 ${younger} 岁`
              : `生物年龄高于实际年龄 ${Math.abs(younger)} 岁`}
          </b>
          <br />
          生物年龄 {data.bioAge} 岁 · 器官年龄 {data.organAge} 岁 · 实际年龄 {data.realAge} 岁。生物年龄反映身体机能
          的实测状态，是本产品最有说服力的正反馈指标之一；它来自功能医学检测与体征数据的联合建模，不等同于临床诊断。
        </div>
      </Card>

      {/* ---------------- 3. 五维雷达 ---------------- */}
      <Card title="五维健康资本结构" more={<span className="card-more">虚线 = 上次评估</span>}>
        {data.dimensions.length === 0 ? (
          <div className="sec-note">本次评估尚未生成五维结构数据。</div>
        ) : (
          <>
            <Chart
              html={charts.radar({
                axes: data.dimensions.map((d) => ({ label: d.label, value: d.value })),
                ref: data.dimensions.map((d) => d.prevValue),
                color: 'h',
              })}
            />
            <div className="divider" />
            {data.dimensions.map((d) => {
              const color = d.value >= 80 ? resolveColor('h') : d.value >= 72 ? resolveColor('e') : resolveColor('warn');
              return (
                <div className="eh-row" key={d.key}>
                  <div className="eh-name">
                    {d.label}
                    <s>权重 {formatPct(d.weight * 100, 0)}</s>
                  </div>
                  <div className="eh-track">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${Math.max(0, Math.min(100, d.value))}%`,
                        background: `linear-gradient(90deg, ${color}, color-mix(in srgb, ${color} 45%, transparent))`,
                      }}
                    />
                  </div>
                  <span className="num" style={{ flex: '0 0 auto', fontSize: 12, color: 'var(--muted-2)' }}>
                    上次 {d.prevValue}
                  </span>
                  <div className="eh-val" style={{ color }}>
                    {d.value}
                  </div>
                </div>
              );
            })}
            <div className="sec-note">
              每维得分为 0–100，权重由 HEW³ 五维模型给定；虚线多边形是上一次评估结果，用于判断进退方向。
            </div>
          </>
        )}
      </Card>

      {/* ---------------- 4. XAI 评分归因（与主分数同屏，不做二级页） ---------------- */}
      <Card title={data.xai.title} more={<Badge tone="e">XAI</Badge>}>
        <div className="row between" style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            本次评分变化 {signed(data.xai.delta)} 分
          </span>
          <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>条长为绝对贡献占比</span>
        </div>

        {data.xai.items.length === 0 ? (
          <div className="sec-note">本次评估暂无可展示的归因项。</div>
        ) : (
          <Chart
            html={charts.hbars({
              items: data.xai.items.map((item) => {
                const negative = item.direction === 'negative' || item.signedValue < 0;
                const share = (Math.abs(item.signedValue) / xaiAbsTotal) * 100;
                return {
                  label: item.label,
                  value: Math.abs(item.signedValue),
                  max: xaiMax,
                  color: negative ? 'danger' : item.color,
                  text: formatPct(share, 1),
                  sub: `${signed(item.signedValue)} 分 · ${negative ? '负向' : '正向'}贡献`,
                };
              }),
            })}
          />
        )}

        <div className="note-src" style={{ marginTop: 6 }}>
          <b>为什么归因与主分数同屏</b>：传统健康 App 只给一个分数，用户无从追问，也无法判断该改什么。HEW³
          在输出 H-Score™ 的同时输出完整归因链路——每一项贡献都带符号与贡献度，负向项用警示色标出，可直接对应到
          干预方案。归因由系统计算，AI 仅做辅助研判，最终结论由人类专家确认。
        </div>
      </Card>

      {/* ---------------- 5. 多模态指标体系（六层 · 描述性） ---------------- */}
      <Card
        title="多模态指标体系"
        more={
          <Badge tone="w">
            {skCaliber.scale.items} 项 / {skCaliber.scale.dims} 维度
          </Badge>
        }
      >
        {data.layers.length === 0 ? (
          <div className="sec-note">尚未接入指标分层数据。</div>
        ) : (
          <>
            {data.layers.map((layer, index) => {
              const color = resolveColor(layer.color);
              return (
                <div key={layer.key} style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                  <div className="row" style={{ gap: 9 }}>
                    <span
                      className="num"
                      style={{
                        flex: '0 0 auto',
                        width: 32,
                        height: 32,
                        borderRadius: 11,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 700,
                        color,
                        background: `color-mix(in srgb, ${color} 12%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{layer.name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', margin: '7px 0 0', lineHeight: 1.7 }}>
                    {layer.desc}
                  </div>
                </div>
              );
            })}
            <div className="sec-note">
              共 {data.layers.length} 层，覆盖 {skCaliber.scale.items} 项指标 / {skCaliber.scale.dims} 维度（
              {skCaliber.scale.breakdown.map((b) => `${b.name} ${b.n}`).join(' · ')}）。
              <b>各层的项数分布未在源材料中给出</b>，因此本页不展示按层分摊的数字 ——
              编造一个分布会让界面看起来精确却毫无依据。异常与关注项以「预警告警」列表为准。
            </div>
          </>
        )}
      </Card>

      {/* ---------------- 6. 三级预警中心 ---------------- */}
      <Card
        title="三级预警中心"
        more={
          data.alerts.length > 0 ? (
            <Badge tone="danger">{data.alerts.length} 项待处理</Badge>
          ) : (
            <Badge tone="ok">暂无待处理</Badge>
          )
        }
      >
        {data.alerts.length === 0 ? (
          <div className="sec-note">当前没有未解除预警。系统持续监测，异常会第一时间提醒您。</div>
        ) : (
          data.alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              busy={acknowledge.isPending && acknowledge.variables === alert.id}
              onAcknowledge={() => acknowledge.mutate(alert.id)}
            />
          ))
        )}

        {data.alertHistory.length > 0 && (
          <>
            <div className="divider" />
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 6 }}>历史预警（已解除）</div>
            {data.alertHistory.map((alert) => (
              <div key={alert.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
                <div className="row between" style={{ marginBottom: 5 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-2)' }}>{alert.title}</span>
                  <Badge tone="ok">已解除</Badge>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
                  {alert.resolvedAt ? `${formatDate(alert.resolvedAt)} 解除 · ` : ''}
                  {alert.resolutionNote ?? alert.desc}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--muted-2)', marginTop: 5 }}>
                  {alert.levelLabel} · {alert.action} · {formatDate(alert.foundAt)} 发现
                </div>
              </div>
            ))}
          </>
        )}
      </Card>

      {/* ---------------- 7. 个性化干预方案 ---------------- */}
      <Card
        title="个性化干预方案"
        more={ongoingGain > 0 ? <Badge tone="h">预计提升 +{ongoingGain.toFixed(1)} 分</Badge> : undefined}
      >
        {data.prescriptions.length === 0 ? (
          <div className="sec-note">暂无推送中的干预方案。</div>
        ) : (
          data.prescriptions.map((item) => {
            const status = prescriptionStatus[item.status];
            return (
              <div key={item.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
                <div className="row between" style={{ marginBottom: 6 }}>
                  <div className="row" style={{ gap: 9, minWidth: 0 }}>
                    <span style={{ fontSize: 17, flex: '0 0 auto' }}>{item.icon}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{item.name}</span>
                  </div>
                  <Badge tone={status.tone}>{status.label}</Badge>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.75 }}>{item.text}</div>
                <div className="row between" style={{ marginTop: 9 }}>
                  <span className="num" style={{ fontSize: 12.5, color: healthColor }}>
                    预计提升 +{item.expectedGain.toFixed(1)} 分
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--muted-2)' }}>
                    {formatDate(item.startedAt)} 起
                  </span>
                </div>
              </div>
            );
          })
        )}
        <div className="sec-note">
          干预方案为健康建议，用于改善可干预的生活方式与行为指标；不构成诊断或治疗方案，具体医疗措施由合作医疗机构执行。
        </div>
      </Card>

      {/* ---------------- 8. 关键生理指标 ---------------- */}
      <Card title="关键生理指标" more={<span className="card-more">参考区间由检测机构给出</span>}>
        {data.vitals.length === 0 ? (
          <div className="sec-note">尚未接入生理指标数据。</div>
        ) : (
          <div style={{ overflowX: 'auto', margin: '0 -2px' }}>
            <div style={{ minWidth: 600 }}>
              <Table
                head={['指标', '当前值', '参考区间', '上次', '状态', '数据来源', '采集时间']}
                rows={data.vitals.map((v) => {
                  const color = vitalColor(v.status);
                  // 待判读（unknown）属信息性状态，不计入「需要注意」，避免把噪声当异常
                  const attention = v.status === 'watch' || v.status === 'risk';
                  return [
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: attention ? color : 'var(--text)' }}>
                      {v.name}
                    </span>,
                    <span className="num" style={{ fontSize: 13.5, fontWeight: 700, color }}>
                      {v.valueText}
                      {v.unit && (
                        <s style={{ fontSize: 11, marginLeft: 3, textDecoration: 'none', color: 'var(--muted-2)' }}>
                          {v.unit}
                        </s>
                      )}
                    </span>,
                    <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{v.refRange}</span>,
                    <span className="num" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {v.prevText}
                    </span>,
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        color,
                        background: `color-mix(in srgb, ${color} 14%, transparent)`,
                      }}
                    >
                      {vitalStatusText[v.status]}
                    </span>,
                    <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{v.source}</span>,
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{formatDate(v.capturedAt)}</span>,
                  ];
                })}
              />
            </div>
          </div>
        )}
        <div className="sec-note">
          「需关注」「异常」行以关注色标出，状态由后端依据参考区间与趋势判定，非人工标注。左右滑动可查看全部列。
        </div>
      </Card>

      {/* ---------------- 9. 检测报告时间轴 ---------------- */}
      <Card title="健康档案 · 检测记录" more={<Badge tone="ghost">{reports.length} 份</Badge>}>
        {reports.length === 0 ? (
          <StateBox title="尚无检测报告" desc="提交一份检测报告后，系统会建立档案并用于后续对比。" />
        ) : (
          <Timeline
            items={reports.map((r) => ({
              id: r.id,
              date: formatDate(r.reportedAt),
              title: (
                <span>
                  {r.title}
                  {r.isCurrent && (
                    <span className="badge h" style={{ marginLeft: 6, fontSize: 11 }}>
                      当前基准
                    </span>
                  )}
                </span>
              ),
              desc: (
                <>
                  {r.institution} · {r.desc}
                  <br />
                  <span style={{ color: r.parsed ? resolveColor('ok') : resolveColor('warn') }}>
                    {r.parsed ? '已完成结构化解析' : '待解析'}
                  </span>
                </>
              ),
              isCurrent: r.isCurrent,
            }))}
          />
        )}
        <Link className="btn block" to="/health/reports" style={{ marginTop: 12 }}>
          查看全部报告与上传入口 ›
        </Link>
      </Card>

      <div className="note-src">
        <b>数据来源与边界</b>：本页 H-Score™ 评估基于 {formatDate(data.measuredAt)} 的{data.dataSource}
        ，结合可穿戴设备与院内检测数据；所有数值均来自后端评估结果，页面不做二次推算。平台不直接开展临床诊疗，
        检测与医疗措施由具备资质的合作医疗机构执行；本页内容不构成诊断或治疗建议，最终方案由人类专家确认。
      </div>
    </div>
  );
}
